import React, { useState, useEffect, useCallback, useContext } from "react";
import studyviewerContext from "../../../../context/studyviewer/StudyviewerContext";
import "../../styles/styles.css";

import { updateViewport } from "../../helpers/CornerstoneViewportService";
import PRListPanel from "./PRListPanel";
import KOListPanel from "./KOListPanel";

// ─── Inline SVG icons ─────────────────────────────────────────────────────────

const UpArrowSVG = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.5 8L6 4.5L9.5 8"
      stroke="white"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DownArrowSVG = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.5 4.5L6 8L9.5 4.5"
      stroke="white"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StarSVG = ({ filled }) =>
  filled ? (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 12.8589L12.944 16L11.632 10.08L16 6.09684L10.248 5.58316L8 0L5.752 5.58316L0 6.09684L4.368 10.08L3.056 16L8 12.8589Z"
        fill="#0085FF"
      />
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 12.8589L12.944 16L11.632 10.08L16 6.09684L10.248 5.58316L8 0L5.752 5.58316L0 6.09684L4.368 10.08L3.056 16L8 12.8589Z"
        fill="none"
        stroke="rgba(255,255,255,0.55)"
        strokeWidth="1.2"
      />
    </svg>
  );

const PencilSVG = ({ active }) => (
  <svg width="13" height="13" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.998047 14.998H3.91431L12.5153 6.39702L9.59908 3.48076L0.998047 12.0818V14.998ZM2.55339 12.7273L9.59908 5.68156L10.3145 6.39702L3.26884 13.4427H2.55339V12.7273Z"
      fill={active ? "#0085FF" : "rgba(255,255,255,0.65)"}
    />
    <path
      d="M12.9508 1.22552C12.6475 0.922224 12.1576 0.922224 11.8543 1.22552L10.4312 2.64865L13.3474 5.56491L14.7706 4.14178C15.0739 3.83849 15.0739 3.34855 14.7706 3.04526L12.9508 1.22552Z"
      fill={active ? "#0085FF" : "rgba(255,255,255,0.65)"}
    />
  </svg>
);

// ─── Widget ───────────────────────────────────────────────────────────────────

/**
 * Vertical navigation widget shown at the bottom-right of each viewport.
 *
 * Layout (top → bottom):
 *   ▲  — scroll to the nearest KO / PR frame above current
 *   ★  — filled when the current frame is a Key Object, outline otherwise
 *   ✏  — shown only when the current frame has PR; clicking opens the PR list popup
 *   ▼  — scroll to the nearest KO / PR frame below current
 *
 * Extra props for the PR list panel (passed from Viewport):
 *   slicePRItems   – PRs relevant to the current slice
 *   onPRToggle     – toggle a single PR on/off
 *   onPRToggleAll  – select / deselect all PRs for this slice
 */
const ViewportNavWidget = ({
  viewportIndex,
  currentImageIndex,
  viewportLoaded,
  priors,
  prAppliedCount,
  currentStack,
  // PR list panel props (per-slice)
  slicePRItems = [],
  onPRToggle,
  onPRToggleAll,
  prItemsLoading = false,
  // KO list popup props (per-slice) — one entry per parsed KO DICOM that
  // references the current slice.  Falls back to the context-derived list
  // when undefined.
  sliceKOItems,
}) => {
  const { studyData, keyImages, priorStudyKeyImages, keyObjectImages, priorStudyKeyObjectImages, prImages, priorStudyPRImages } =
    useContext(studyviewerContext);

  const [markedFrames,    setMarkedFrames]    = useState([]);
  const [isCurrentKO,     setIsCurrentKO]     = useState(false);
  const [isCurrentPR,     setIsCurrentPR]     = useState(false);
  const [prListOpen,      setPrListOpen]      = useState(false);
  const [koListOpen,      setKoListOpen]      = useState(false);
  // All Key Object entries that reference the currently visible slice.
  // Populated whenever the current frame changes.
  const [currentSliceKOs, setCurrentSliceKOs] = useState([]);
  // Series-level flags — whether the SERIES (not just the current frame) has any
  // KO / PR image.  Used to decide whether to render the ★ / ✏ icons at all.
  const [seriesHasKO,     setSeriesHasKO]     = useState(false);
  const [seriesHasPR,     setSeriesHasPR]     = useState(false);

  // ── Compute the union of KO + PR marked frame indices ────────────────────

  const computeMarkedFrames = useCallback(() => {
    if (!currentStack?.imageIds?.length) return;

    const koMarked = new Set();
    const prMarked = new Set();

    // KO frames come from BOTH the legacy key_image_title-derived keyImages AND
    // the dedicated key_object_description-derived keyObjectImages. The viewer KO
    // star must render off the latter too, otherwise images marked as Key Objects
    // (which now write to key_object_description, not key_image_title) show no star.
    const allKI = [
      ...(keyImages ?? []),
      ...(keyObjectImages ?? []),
      ...(priors ? (priorStudyKeyImages ?? []) : []),
      ...(priors ? (priorStudyKeyObjectImages ?? []) : []),
    ].filter((ki) => ki.state !== "DELETED");
    const koUrlSet = new Set(allKI.map((ki) => ki.imageUrl));
    currentStack.imageIds.forEach((id, i) => { if (koUrlSet.has(id)) koMarked.add(i); });

    // Include the PARENT study's PR images (prImages) as well as the priors'
    // (priorStudyPRImages) when in the patient viewer — mirroring the KO union
    // above. Previously priors mode used ONLY priorStudyPRImages, so a parent
    // study's PR pencil never appeared in the patient viewer (matching is by
    // image URL, so a series only ever matches its own study's PR images).
    const allPR = [
      ...(prImages ?? []),
      ...(priors ? (priorStudyPRImages ?? []) : []),
    ];
    const prUrlSet = new Set(allPR.map((p) => p.imageUrl));
    currentStack.imageIds.forEach((id, i) => { if (prUrlSet.has(id)) prMarked.add(i); });

    setMarkedFrames([...new Set([...koMarked, ...prMarked])].sort((a, b) => a - b));
    setSeriesHasKO(koMarked.size > 0);
    setSeriesHasPR(prMarked.size > 0);
  }, [currentStack, keyImages, priorStudyKeyImages, keyObjectImages, priorStudyKeyObjectImages, priors, prImages, priorStudyPRImages]);

  useEffect(() => {
    if (viewportLoaded) computeMarkedFrames();
  }, [viewportLoaded, computeMarkedFrames]);

  // ── Update per-frame KO / PR status whenever the frame changes ───────────

  useEffect(() => {
    if (!currentStack || !viewportLoaded) return;

    const currentImageId = currentStack.imageIds?.[currentImageIndex];

    const allKI = [
      ...(keyImages ?? []),
      ...(keyObjectImages ?? []),
      ...(priors ? (priorStudyKeyImages ?? []) : []),
      ...(priors ? (priorStudyKeyObjectImages ?? []) : []),
    ].filter((ki) => ki.state !== "DELETED");

    // KO list — prefer the parsed-from-DICOM list passed in as sliceKOItems
    // (one row per KO DICOM, supports multiple KOs per slice).  Fall back to
    // the context-derived list (keyImages + keyObjectImages) when the parent
    // hasn't supplied it.
    const slicesKOs = Array.isArray(sliceKOItems) && sliceKOItems.length > 0
      ? sliceKOItems
      : (currentImageId
          ? allKI
              .filter((ki) => ki.imageUrl === currentImageId)
              .map((ki) => ({
                title:       ki.keyImageTitle || "",
                description: ki.description   || "",
                creatorName: ki.creatorName   || "",
                date:        ki.date          || "",
              }))
          : []);
    setCurrentSliceKOs(slicesKOs);
    // Current frame is "KO" when EITHER the parsed list has entries OR the
    // legacy context flag matches — keeps the star showing even if the user
    // reaches the frame before the KO DICOM has been parsed.
    setIsCurrentKO(slicesKOs.length > 0 ||
      (!!currentImageId && allKI.some((ki) => ki.imageUrl === currentImageId)));

    // Include the PARENT study's PR images (prImages) as well as the priors'
    // (priorStudyPRImages) when in the patient viewer — mirroring the KO union
    // above. Previously priors mode used ONLY priorStudyPRImages, so a parent
    // study's PR pencil never appeared in the patient viewer (matching is by
    // image URL, so a series only ever matches its own study's PR images).
    const allPR = [
      ...(prImages ?? []),
      ...(priors ? (priorStudyPRImages ?? []) : []),
    ];
    setIsCurrentPR(!!currentImageId && allPR.some((p) => p.imageUrl === currentImageId));
  }, [currentImageIndex, viewportLoaded, currentStack, keyImages, priorStudyKeyImages, keyObjectImages, priorStudyKeyObjectImages, priors, prImages, priorStudyPRImages, sliceKOItems]);

  // Close any open popup ONLY when the user scrolls to a different frame.
  // Kept separate from the per-frame status effect above so unrelated prop
  // re-references (e.g. parent re-renders producing a new sliceKOItems array)
  // don't slam the popup shut the instant the user opens it.
  useEffect(() => {
    setPrListOpen(false);
    setKoListOpen(false);
  }, [currentImageIndex]);

  // ── Navigation helpers ────────────────────────────────────────────────────

  // idx is a SERIES (instance) index. updateViewport handles a multi-frame clip
  // playing or paused on a frame (INNER stack), where a raw setImageIdIndex
  // would move to a FRAME of the clip instead of the marked instance.
  const scrollToFrame = async (idx) => {
    await updateViewport(viewportIndex, idx);
  };

  const prevFrame = markedFrames.filter((i) => i < currentImageIndex).at(-1);
  const nextFrame = markedFrames.find((i) => i > currentImageIndex);

  // Pencil is "active" (blue) when at least one PR on this slice is selected.
  const pencilActive = slicePRItems.length > 0
    ? slicePRItems.some((i) => i.selected)
    : true; // default active if no slice data yet

  // ── Render guard ──────────────────────────────────────────────────────────

  if (
    !viewportLoaded ||
    !currentStack ||
    currentStack.modality === "PR" ||
    currentStack.modality === "SR"
  ) return null;

  // Hide the entire widget only when the SERIES has neither KO nor PR.
  if (!seriesHasKO && !seriesHasPR) return null;

  return (
    <div className="viewport-nav-widget" onDoubleClick={(e) => e.stopPropagation()}>
      {/* ▲ Up — previous marked frame */}
      <span
        title="Previous Key Object / PR image"
        className={`viewport-nav-icon${prevFrame === undefined ? " disabled" : ""}`}
        onClick={() => prevFrame !== undefined && scrollToFrame(prevFrame)}
      >
        <UpArrowSVG />
      </span>

      {/* ★ Star — opens the KO list popup when the current slice has Key Objects;
          shown but disabled (outline) when the slice is not a KO. */}
      {seriesHasKO && (
        <div style={{ position: "relative" }}>
          <span
            title={
              !isCurrentKO ? "Not a Key Object"
              : koListOpen  ? "Close Key Objects"
              :               currentSliceKOs.length > 1
                                ? `${currentSliceKOs.length} Key Objects on this slice`
                                : "Key Object"
            }
            className={`viewport-nav-icon${isCurrentKO ? "" : " disabled"}`}
            onClick={isCurrentKO ? () => setKoListOpen((v) => !v) : undefined}
            style={{ cursor: isCurrentKO ? "pointer" : "default" }}
          >
            <StarSVG filled={isCurrentKO} />
          </span>

          {koListOpen && isCurrentKO && (
            <KOListPanel
              items={currentSliceKOs}
              onClose={() => setKoListOpen(false)}
              style={{
                right:  "calc(100% + 6px)",
                bottom: "-4px",
                left:   "auto",
                top:    "auto",
              }}
            />
          )}
        </div>
      )}

      {/* ✏ Pencil — shown if the series has any PR.  When the current slice
          is NOT a PR-referenced frame, the pencil is rendered in disabled
          state (grey, no click) — matching the KO star behaviour. */}
      {seriesHasPR && (
        <div style={{ position: "relative" }}>
          <span
            title={
              !isCurrentPR ? "No Presentation State on this slice"
              : prListOpen  ? "Close Presentation States"
              :               "Presentation States for this slice"
            }
            className={`viewport-nav-icon${isCurrentPR ? "" : " disabled"}`}
            onClick={isCurrentPR ? () => setPrListOpen((v) => !v) : undefined}
            style={{ cursor: isCurrentPR ? "pointer" : "default" }}
          >
            <PencilSVG active={isCurrentPR && pencilActive} />
          </span>

          {prListOpen && isCurrentPR && (
            <PRListPanel
              items={slicePRItems}
              loading={prItemsLoading}
              onToggle={onPRToggle}
              onToggleAll={(selectAll) => onPRToggleAll?.(selectAll, slicePRItems)}
              onClose={() => setPrListOpen(false)}
              style={{
                // Position the panel to the LEFT of the nav widget so it
                // doesn't go off-screen on the right edge.
                right:        "calc(100% + 6px)",
                bottom:       "-4px",
                left:         "auto",
                top:          "auto",
              }}
            />
          )}
        </div>
      )}

      {/* ▼ Down — next marked frame */}
      <span
        title="Next Key Object / PR image"
        className={`viewport-nav-icon${nextFrame === undefined ? " disabled" : ""}`}
        onClick={() => nextFrame !== undefined && scrollToFrame(nextFrame)}
      >
        <DownArrowSVG />
      </span>
    </div>
  );
};

export default ViewportNavWidget;
